import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { useHasPermission } from "./session-context"
import { AbilityIcon, SearchIcon } from "./components/icons"
import { Select } from "./components/select"
import { StatusTabs } from "./components/status-tabs"
import { ContentPagination } from "./content-components"

type BeneficiaryStatus = "nominated" | "verified" | "active" | "concluded"
type BeneficiaryCategory = "mobility" | "visual" | "hearing" | "limb_difference" | "other"
type BeneficiaryAgeGroup = "child" | "teen" | "university" | "adult"
const PAGE_SIZE = 10

interface BeneficiaryListItem {
  id: string
  code: string
  fullName: string
  ageGroup: BeneficiaryAgeGroup
  category: BeneficiaryCategory
  state: string
  status: BeneficiaryStatus
  sponsorName: string | null
  photoUrl: string | null
}

interface StatusCounts {
  all: number
  nominated: number
  verified: number
  active: number
  concluded: number
}

const STATUS_COLORS: Record<BeneficiaryStatus, { bg: string; text: string }> = {
  nominated: { bg: "bg-orange-100", text: "text-orange-700" },
  verified: { bg: "bg-blue-100", text: "text-blue-700" },
  active: { bg: "bg-green-100", text: "text-green-700" },
  concluded: { bg: "bg-gray-100", text: "text-gray-700" },
}

const CATEGORY_LABELS: Record<BeneficiaryCategory, string> = {
  mobility: "Mobility",
  visual: "Visual",
  hearing: "Hearing",
  limb_difference: "Limb difference",
  other: "Other",
}

const AGE_GROUP_LABELS: Record<BeneficiaryAgeGroup, string> = {
  child: "Child",
  teen: "Teen",
  university: "University",
  adult: "Adult",
}

const CATEGORY_OPTIONS = ["All", "Mobility", "Visual", "Hearing", "Limb difference", "Other"] as const
const AGE_GROUP_OPTIONS = ["All", "Child", "Teen", "University", "Adult"] as const

function categoryFromLabel(label: string): BeneficiaryCategory | undefined {
  const map: Record<string, BeneficiaryCategory> = {
    Mobility: "mobility",
    Visual: "visual",
    Hearing: "hearing",
    "Limb difference": "limb_difference",
    Other: "other",
  }
  return map[label]
}

function ageGroupFromLabel(label: string): BeneficiaryAgeGroup | undefined {
  const map: Record<string, BeneficiaryAgeGroup> = {
    Child: "child",
    Teen: "teen",
    University: "university",
    Adult: "adult",
  }
  return map[label]
}

function getAgeFromGroup(ageGroup: BeneficiaryAgeGroup): string {
  const ageRanges: Record<BeneficiaryAgeGroup, string> = {
    child: "09",
    teen: "14",
    university: "22",
    adult: "34",
  }
  return ageRanges[ageGroup]
}

function AbilityPageSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="h-8 w-48 rounded bg-gold-500/30" />
          <div className="mt-2 h-4 w-64 rounded bg-gold-500/20" />
        </div>
        <div className="h-10 w-36 rounded-full bg-gold-500/30" />
      </div>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-10 w-24 rounded-full bg-gold-500/20" />
        ))}
      </div>
      <div className="rounded-[10px] border border-gold-500 bg-white">
        <div className="space-y-4 p-5">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-gold-500/20" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-32 rounded bg-gold-500/20" />
                <div className="h-3 w-24 rounded bg-gold-500/10" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function AbilityPage() {
  const navigate = useNavigate()
  const canView = useHasPermission("ability")
  const canMutate = useHasPermission("ability_mutate")
  const [beneficiaries, setBeneficiaries] = useState<BeneficiaryListItem[]>([])
  const [counts, setCounts] = useState<StatusCounts>({ all: 0, nominated: 0, verified: 0, active: 0, concluded: 0 })
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<BeneficiaryStatus | "all">("all")
  const [search, setSearch] = useState("")
  const [categoryFilter, setCategoryFilter] = useState<string>("All")
  const [ageGroupFilter, setAgeGroupFilter] = useState<string>("All")

  const fetchBeneficiaries = async () => {
    try {
      const query: { status?: BeneficiaryStatus; category?: BeneficiaryCategory; ageGroup?: BeneficiaryAgeGroup; search?: string; limit: string; offset: string } = {
        limit: String(PAGE_SIZE),
        offset: String((page - 1) * PAGE_SIZE),
      }
      if (activeTab !== "all") query.status = activeTab
      if (search.trim()) query.search = search.trim()
      const category = categoryFromLabel(categoryFilter)
      if (category) query.category = category
      const ageGroup = ageGroupFromLabel(ageGroupFilter)
      if (ageGroup) query.ageGroup = ageGroup

      const res = await client.api.admin.ability.$get({ query })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        setBeneficiaries(body.data.items)
        setCounts(body.data.counts)
        setTotal(body.data.total)
      }
    } catch {
      setError("Failed to load beneficiaries")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (canView) {
      void fetchBeneficiaries()
    } else {
      setLoading(false)
    }
  }, [activeTab, categoryFilter, ageGroupFilter, canView, page])

  useEffect(() => {
    setPage(1)
  }, [activeTab, categoryFilter, ageGroupFilter, search])

  // Debounced search
  useEffect(() => {
    if (!canView) return
    const timer = setTimeout(() => {
      void fetchBeneficiaries()
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">Ability Program</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view ability beneficiaries.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <AbilityPageSkeleton />
  }

  const tabs: { key: BeneficiaryStatus | "all"; label: string; count: number }[] = [
    { key: "all", label: "All", count: counts.all },
    { key: "verified", label: "Verified", count: counts.verified },
    { key: "nominated", label: "Nominated", count: counts.nominated },
    { key: "active", label: "Active", count: counts.active },
    { key: "concluded", label: "Concluded", count: counts.concluded },
  ]

  return (
    <div className="space-y-6">
      {/* Row 1: Title + Add button */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">Ability Program</h1>
          <p className="mt-1 text-sm text-ink/60">{counts.all} beneficiaries in the program.</p>
        </div>
        {canMutate && (
          <button
            onClick={() => navigate("/admin/ability/new")}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-forest-950 px-5 text-sm font-medium text-white transition-colors hover:bg-forest-900"
          >
            <span className="text-base">+</span>
            Add beneficiary
          </button>
        )}
      </div>

      {/* Row 2: Status tabs */}
      <StatusTabs
        tabs={tabs}
        activeKey={activeTab}
        onChange={setActiveTab}
        ariaLabel="Ability beneficiary status"
      />

      {/* Row 3: Search + Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
          <input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-11 w-full rounded-full border border-gold-500 bg-cream-50 pl-11 pr-4 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-3">
          <div className="w-28 flex-shrink-0">
            <Select
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={CATEGORY_OPTIONS}
              placeholder="All"
            />
          </div>
          <div className="w-28 flex-shrink-0">
            <Select
              value={ageGroupFilter}
              onChange={setAgeGroupFilter}
              options={AGE_GROUP_OPTIONS}
              placeholder="All"
            />
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error}</p>
        </div>
      ) : beneficiaries.length === 0 ? (
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">No beneficiaries found</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gold-500/50">
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Beneficiary
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Age Group
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Category
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Location
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Status
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Sponsor
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gold-500/30">
                {beneficiaries.map((beneficiary) => (
                  <tr
                    key={beneficiary.id}
                    onClick={() => navigate(`/admin/ability/${beneficiary.id}`)}
                    className="cursor-pointer hover:bg-cream-50"
                  >
                    <td className="px-5 py-4">
                      <div>
                        <p className="text-sm font-medium text-ink">{beneficiary.fullName}</p>
                        <p className="text-xs text-ink/50">Age {getAgeFromGroup(beneficiary.ageGroup)} · {beneficiary.code}</p>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-ink">{AGE_GROUP_LABELS[beneficiary.ageGroup]}</td>
                    <td className="px-5 py-4 text-sm text-ink">{CATEGORY_LABELS[beneficiary.category]}</td>
                    <td className="px-5 py-4 text-sm text-ink">{beneficiary.state}</td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
                          STATUS_COLORS[beneficiary.status].bg
                        } ${STATUS_COLORS[beneficiary.status].text}`}
                      >
                        {beneficiary.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm text-ink">
                      {beneficiary.sponsorName || <span className="text-ink/40">----</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ContentPagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} disabled={loading} />
    </div>
  )
}
